import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TableContainer,
  TableContent,
  CELL_RENDERERS,
  TableHeaderCustomContent,
  IconCheckCircle,
  Loader,
  StatusIndicator,
  Dialog,
  TextRenderer,
  Divider,
  Button,
  IconOpenInNew,
  BUTTON_VARIANTS,
} from 'cdk-radial';
import classNames from 'classnames';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import { AgGridReact } from 'ag-grid-react';
import { getSignedS3Url } from '../../../../../../../redux/connectedComponent/solutionInfo/solutionInfo.slice';
import '../../styles/ReviewTerms.scss';
import { CONSTANTS } from '../../constants';
import {
  disableNext,
  saveApiConsentForSelectedLocations,
} from '../../../../../../../redux/connectedComponent/ManageIntegrationsV2/ManageIntegrationsV2.slice';

export const TermsButton = ({
  addendumDoc,
  apiDetails,
  integrationsDataPerLocation,
  solutionData,
  subscriptionData,
}) => {
  const dispatch = useDispatch();
  const {
    requestedIntegrationStatuses,
    consentCompletedLocations,
    isConsentSaveSuccess,
  } = useSelector((state) => state.ManageIntegrationsV2Slice);

  const { entityIdNameMapping, role, authenticationToken } = useSelector(
    (state) => state.loginDetailsSlice
  );

  const { customConsentFormSignedS3Url, getSignedS3UrlError } = useSelector(
    (state) => state.solutionInfoSlice
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [locationsToBeConsented, setLocationsToBeConsented] = useState([]);
  const hasAddendum =
    !!addendumDoc ||
    (solutionData.custom_consent &&
      (!solutionData.custom_consent.selectedApis ||
        solutionData.custom_consent.selectedApis.includes(
          apiDetails?.id?.replace('api-v2-', '')
        )));
  const DARK_GREEN = 'rgb(9, 173, 0)';
  const LIGHT_GREEN = 'rgb(205, 240, 204)';

  let requestedLocations = [];
  for (const entityId of Object.keys(requestedIntegrationStatuses)) {
    if (requestedIntegrationStatuses[entityId] === 'enable') {
      requestedLocations.push({
        entityId: entityId,
        locationName: entityIdNameMapping[entityId],
      });
    }
  }

  useEffect(() => {
    const locationsToBeConsented = requestedLocations?.filter(
      (f) => consentCompletedLocations?.indexOf(f.entityId) === -1
    );
    setLocationsToBeConsented(locationsToBeConsented);
  }, [consentCompletedLocations]);

  useEffect(() => {
    if (isConsentSaveSuccess) {
      setIsLoading(false);
    }
  }, [isConsentSaveSuccess]);

  useEffect(() => {
    if (
      solutionData.custom_consent &&
      (!solutionData.custom_consent.selectedApis ||
        solutionData.custom_consent.selectedApis.includes(
          apiDetails?.id?.replace('api-v2-', '')
        ))
    ) {
      const consentUrl =
        solutionData.custom_consent.customConsentForm.split('.com/');
      const customConsentFileName = (consentUrl && consentUrl[1]) || '';
      dispatch(
        getSignedS3Url({
          type: 'customConsentForm',
          expires: 3600,
          fileName: encodeURIComponent(customConsentFileName),
          token: authenticationToken,
        })
      );
    }
  }, []);

  useEffect(() => {
    if ((locationsToBeConsented && locationsToBeConsented?.length > 0) || getSignedS3UrlError) {
      dispatch(disableNext(true));
    } else {
      dispatch(disableNext(false));
    }
  }, [locationsToBeConsented?.length, getSignedS3UrlError]);

  const enabledLocations = requestedLocations.map((f) => {
    const subscriptionsData = subscriptionData.find(
      (m) => m.entityId === f.entityId
    );
    return {
      entityId: f.entityId,
      cmfNumber: subscriptionsData.entitycmfNumber,
    };
  });

  const preparePayload = () => {
    const { id, apiType } = apiDetails;
    const integrationsData = integrationsDataPerLocation[0];
    const apiData = solutionData?.registration_details?.api_details?.find(
      (api) => api?.id?.replace('api-v2-', '') === id?.replace('api-v2-', '')
    );
    const providerId = apiData?.providers && apiData?.providers[0]?.entityId;

    let roleData = {};
    let locations = [];
    requestedLocations.map((f) => {
      locations.push(f.locationName);
    });
    roleData.dealershipName = locations.toString();
    const payload = {
      api: id,
      apiProviderId: providerId || apiDetails?.id,
      apiType: apiType,
      asyncIntegrationId:
        integrationsData?.asyncIntegrationId ||
        apiData?.asyncIntegrationId ||
        null,
      businessTitle: role,
      businessRoleData: roleData,
      consentType: 'api_data',
      consentedSource: 'dealerportal',
      customConsentForm:
        solutionData.custom_consent &&
        (!solutionData.custom_consent.selectedApis ||
          solutionData.custom_consent.selectedApis.includes(
            apiDetails.api.replace('api-v2-', '')
          )) &&
        solutionData.custom_consent.customConsentForm,
      documentLink: addendumDoc || null,
      enabledEntityIds: enabledLocations,
      listingVersion: solutionData.listingVersion,
      reasonForRejection: null,
      signedTermsAndConditions: null,
      solutionId: solutionData.id,
      solutionVersion: null,
      status: 'accepted',
      subscriptionStatus: 'completed',
    };

    const solution = {
      id: solutionData.id,
      listingVersion: solutionData.listingVersion,
    };

    return { consent: payload, solution: solution };
  };

  const onTermsAccepted = () => {
    if (hasAddendum) {
      setIsOpen(true);
    } else {
      setIsLoading(true);
      const payload = preparePayload();
      dispatch(
        saveApiConsentForSelectedLocations({
          solution: payload.solution,
          consent: payload.consent,
          token: authenticationToken,
          locations: requestedLocations,
        })
      );
    }
  };

  const handleAcceptTerms = () => {
    setIsOpen(false);
    setIsLoading(true);
    const payload = preparePayload();
    dispatch(
      saveApiConsentForSelectedLocations({
        solution: payload.solution,
        consent: payload.consent,
        token: authenticationToken,
        locations: requestedLocations,
      })
    );
  };

  const handleClose = () => {
    setIsOpen(false);
  };
  const renderCustomFooter = () => {
    return (
      <div className="custom-footer">
        <div className="confirmation-text">
          {CONSTANTS.REVIEW_TERMS_FOOTER_TEXT}
        </div>
        <div>
          <span className="consent-dialog_button">
            <Button
              className="consent-dialog_button"
              text="No"
              onClick={handleClose}
              variant={BUTTON_VARIANTS.SECONDARY}
            ></Button>
          </span>
          <span className="consent-dialog_button">
            <Button
              text="Yes"
              onClick={handleAcceptTerms}
              variant={BUTTON_VARIANTS.PRIMARY}
              className="consent-dialog_button"
            ></Button>
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      {!locationsToBeConsented?.length ? (
        <StatusIndicator
          backgroundColor={LIGHT_GREEN}
          borderColor={DARK_GREEN}
          icon={<IconCheckCircle />}
          iconColor={DARK_GREEN}
          label="Terms Accepted"
          variant="custom"
        />
      ) : (
        <Button
          backgroundAppearance="light"
          iconColor="primary"
          iconSize={28}
          onClick={onTermsAccepted}
          size="standard"
          icon={hasAddendum ? <IconOpenInNew /> : null}
          text={hasAddendum ? 'Review Terms' : 'Accept Terms'}
          variant={BUTTON_VARIANTS.SECONDARY}
        />
      )}
      <Dialog
        isOpen={isOpen}
        onClose={handleClose}
        title={CONSTANTS.TERMS_AND_CONDITIONS}
        id="consent-dialog"
        className="consent_dialog"
        renderCustomFooterComponent={renderCustomFooter}
      >
        <div className={'consent-pdf-container'}>
          <embed
            type="application/pdf"
            src={
              solutionData.custom_consent &&
              (!solutionData.custom_consent.selectedApis ||
                solutionData.custom_consent.selectedApis.includes(
                  apiDetails?.id?.replace('api-v2-', '')
                ))
                ? customConsentFormSignedS3Url
                : addendumDoc
            }
            style={{ width: '100%', height: 'inherit' }}
          />
        </div>
      </Dialog>
      <div>
        <Dialog
          isOpen={isLoading && !isConsentSaveSuccess}
          id="loading-overlay-id"
          hideCloseButton
          hideHeader
        >
          <div className="plan-terms-dialog-body-container loader">
            <Loader
              open={isLoading && !isConsentSaveSuccess}
              className="plan-terms-dialog-body-container-loader"
            />
          </div>
          <div className="plan-terms-dialog-header">
            {CONSTANTS.ACCEPT_TERMS_LOADER_TEXT}
          </div>
        </Dialog>
      </div>
    </>
  );
};

export const ReviewTermsTable = ({
  apiDetails,
  integrationsDataPerLocation = [],
  solutionData,
  subscriptionData,
}) => {
  const { apiDisplayName, name, addendumDoc } = apiDetails;

  const [gridApi, setGridApi] = useState({});
  const [rowData, setRowData] = useState([]);

  const frameworkComponentsExtended = {
    textRenderer: TextRenderer,
  };

  const componentMainClass = 'review_terms_table';
  const agGridThemeClass = 'ag-theme-balham';
  const tableClass = classNames(componentMainClass, agGridThemeClass);

  const columns = [
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'apiName',
      headerName: CONSTANTS.API_NAME,
      width: 200,
      cellClass: 'tooltip-truncate',
      tooltipField: 'apiName',
    },
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'provider',
      headerName: 'Provider',
      width: 200,
      cellClass: 'tooltip-truncate',
      tooltipField: 'provider',
    },
    {
      cellRendererFramework: TermsButton,
      field: 'terms',
      headerName: 'Terms',
      minWidth: 180,
      width: 150,
      cellClass: 'tooltip-truncate',
      tooltipField: 'terms',
      suppressSizeToFit: true,
      cellRendererParams: {
        addendumDoc,
        apiDetails,
        integrationsDataPerLocation,
        solutionData,
        subscriptionData,
      },
    },
  ];
  const onGridReady = useCallback((gridParams) => {
    setGridApi(gridParams.api);
    gridParams.api.sizeColumnsToFit();
    const data = [
      {
        apiName: apiDisplayName,
        provider: name,
      },
    ];
    setRowData(data);
  }, []);

  return (
    <TableContainer className={tableClass}>
      <TableHeaderCustomContent
        className="custom-header"
        customHeaderContent={
          <>
            <h1 className="review_terms_header_heading">
              {CONSTANTS.REVIEW_TERMS_HEADER}
            </h1>
            <p className="review_terms_header_subheading">
              {CONSTANTS.REVIEW_TERMS_HEADER_DESCRIPTION}
            </p>
          </>
        }
      />
      <TableContent>
        <Divider></Divider>
        <AgGridReact
          key={`${columns.map((c) => c.headerName)}`}
          columnDefs={columns}
          rowData={rowData}
          frameworkComponents={frameworkComponentsExtended}
          suppressRowHoverHighlight={true}
          onGridReady={onGridReady}
          rowHeight={80}
          rowStyle={{ background: 'white' }}
          domLayout="autoHeight"
        />
      </TableContent>
    </TableContainer>
  );
};
